import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";

export interface ConsentOnThisPageItem {
  id: string;
  title: string;
  summary?: string;
}

/**
 * Sticky jump links for the Consent Banner sections, with a one-line summary of each. Styled like the outline menus on
 * the US State Notices and Data Flows pages, and placed left of the content like them.
 * @param topOffset Height of everything fixed or sticky over the top of the page (app-wide banners and the environment
 * header), in px.
 */
export const ConsentOnThisPage = ({
  items,
  topOffset = 0,
}: {
  items: ConsentOnThisPageItem[];
  topOffset?: number;
}) => {
  const [activeId, setActiveId] = useState(items[0]?.id);
  const navRef = useRef<HTMLElement>(null);
  const itemIds = items.map((item) => item.id).join(",");

  useEffect(() => {
    // Pages scroll inside `.page-content`, not the window.
    const root = navRef.current?.closest(".page-content") ?? null;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      // A section is "current" once its top passes under the sticky environment header.
      { root, rootMargin: `-${topOffset + 28}px 0px -55% 0px` },
    );

    itemIds.split(",").forEach((id) => {
      const section = document.getElementById(id);
      if (section) {
        observer.observe(section);
      }
    });

    return () => observer.disconnect();
  }, [itemIds, topOffset]);

  return (
    <nav
      ref={navRef}
      aria-label="On this page"
      className="sticky top-[calc(var(--consent-sticky-top,0px)_+_var(--consent-header-height,112px)_+_24px)]"
    >
      <h2 className="m-0 text-[14px] font-medium text-neutral-900">On this page</h2>
      <ol className="mb-0 mt-1.5 list-none p-0">
        {items.map((item) => {
          const isActive = item.id === activeId;
          return (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                aria-current={isActive ? "location" : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById(item.id)
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  setActiveId(item.id);
                }}
                className={clsx(
                  "block rounded-[8px] px-[12px] py-[9px] text-[12px] font-medium no-underline hover:no-underline",
                  isActive ? "bg-neutral-100 text-neutral-900" : "text-neutral-600",
                )}
              >
                {item.title}
                {item.summary && (
                  <span
                    title={item.summary}
                    className="mt-[2px] block truncate font-normal text-neutral-500"
                  >
                    {item.summary}
                  </span>
                )}
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
